import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import * as LucideIcons from 'lucide-react';
import { useAppContext } from '../../AppContext';
import { useTranslation } from 'react-i18next';

const CoopRaidBoss = ({ nexusId, onBossClick }) => {
    const { t } = useTranslation();
    const { db, appId } = useAppContext();
    const [raidBoss, setRaidBoss] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!db || !nexusId || !appId) return;

        const q = query(
            collection(db, `artifacts/${appId}/public/data/nexus_raids`),
            where('nexusId', '==', nexusId),
            where('status', '==', 'active'),
            limit(1)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                setRaidBoss({ id: doc.id, ...doc.data() });
            } else {
                setRaidBoss(null);
            }
            setLoading(false);
        }, (err) => {
            console.error("Error fetching Raid Boss:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, nexusId, appId]);

    if (loading) {
        return (
            <div className="w-full h-32 bg-gray-900/50 animate-pulse rounded-2xl border border-white/5 mb-8"></div>
        );
    }

    if (!raidBoss) {
        return (
            <div className="w-full p-6 bg-gradient-to-r from-gray-900 to-black rounded-2xl border border-dashed border-gray-700 mb-8 text-center shadow-lg">
                <LucideIcons.ShieldCheck size={32} className="mx-auto text-green-500 mb-3" />
                <h3 className="text-xl font-bold text-gray-300">Nexus is Secure</h3>
                <p className="text-sm text-gray-500">No active threats detected. Play games to spawn a new Raid Boss!</p>
            </div>
        );
    }

    const hpPercentage = (raidBoss.currentHp / raidBoss.maxHp) * 100;
    
    // Determine boss colors based on type
    let bossColors = "from-red-600 to-purple-900";
    let iconColor = "text-red-500";
    let BossIcon = LucideIcons.Skull;

    if (raidBoss.type === 'Synthetic') {
        bossColors = "from-cyan-600 to-blue-900";
        iconColor = "text-cyan-400";
        BossIcon = LucideIcons.Cpu;
    } else if (raidBoss.type === 'Cosmic') {
        bossColors = "from-fuchsia-600 to-indigo-900";
        iconColor = "text-fuchsia-400";
        BossIcon = LucideIcons.Orbit;
    } else if (raidBoss.type === 'Ethereal') {
        bossColors = "from-teal-600 to-emerald-900";
        iconColor = "text-teal-400";
        BossIcon = LucideIcons.Ghost;
    }

    return (
        <div 
            onClick={onBossClick}
            className={`relative w-full p-6 bg-gradient-to-br ${bossColors} rounded-2xl border border-white/20 mb-8 overflow-hidden shadow-2xl cursor-pointer transform hover:scale-[1.02] transition-all group`}
        >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-0"></div>
            
            {/* Pulsing warning light */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/20 rounded-full blur-3xl animate-ping z-0 pointer-events-none"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-black/60 rounded-xl border border-white/20 flex items-center justify-center shadow-inner relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-50 z-10"></div>
                        <BossIcon size={40} className={`${iconColor} relative z-20 animate-pulse`} />
                    </div>
                </div>

                <div className="flex-1 w-full">
                    <div className="flex justify-between items-end mb-2">
                        <div>
                            <span className={`text-xs font-black uppercase tracking-widest ${iconColor} bg-black/40 px-2 py-0.5 rounded border border-white/10 mb-1 inline-block`}>
                                ACTIVE THREAT: {raidBoss.type}
                            </span>
                            <h3 className="text-2xl md:text-3xl font-extrabold text-white font-orbitron truncate tracking-wide drop-shadow-md">
                                {raidBoss.name}
                            </h3>
                        </div>
                        <div className="text-right">
                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider block mb-1">Integrity</span>
                            <span className="text-lg font-bold text-white drop-shadow-md">
                                {raidBoss.currentHp.toLocaleString()} <span className="text-sm text-gray-400 font-normal">/ {raidBoss.maxHp.toLocaleString()}</span>
                            </span>
                        </div>
                    </div>

                    {/* HP Bar */}
                    <div className="w-full h-4 bg-black/80 rounded-full overflow-hidden border border-white/10 shadow-inner relative">
                        <div 
                            className={`h-full transition-all duration-1000 ease-out bg-gradient-to-r from-red-600 via-orange-500 to-yellow-400`}
                            style={{ width: `${hpPercentage}%` }}
                        ></div>
                        {/* Shimmer effect */}
                        <div className="absolute top-0 left-0 h-full w-12 bg-white/30 skew-x-12 animate-[shimmer_2s_infinite]"></div>
                    </div>
                    
                    <p className="text-center md:text-left mt-3 text-sm text-gray-300 font-medium">
                        Play any arcade game below to deal damage. Defeat the boss to earn a Relic for your Nexus!
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CoopRaidBoss;
